import { z } from 'zod';
import { RetentionStatusSchema } from './enums.js';

export const EvidenceFileSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  assessmentId: z.string().nullable(),
  responseId: z.string().nullable(),
  corpusItemId: z.string().nullable(),
  fileName: z.string(),
  contentType: z.string(),
  sizeBytes: z.number().int(),
  sha256: z.string(),
  uploadedBy: z.string(),
  uploadedAt: z.string(),
  supersededById: z.string().nullable(),
  retentionStatus: RetentionStatusSchema,
  locked: z.boolean(),
  /** Row this attachment was linked from (null for direct uploads). */
  sourceEvidenceId: z.string().nullable(),
});
export type EvidenceFile = z.infer<typeof EvidenceFileSchema>;

export const LinkEvidenceInputSchema = z.object({ evidenceId: z.string().min(1) });
export type LinkEvidenceInput = z.infer<typeof LinkEvidenceInputSchema>;

/**
 * Retrospective de-duplication of evidence uploaded before linking existed
 * (system admin). Dry run by default: `apply` must be set to change anything.
 */
export const EvidenceDeduplicationInputSchema = z.object({
  /** Limit to one organisation; omitted = every organisation. */
  orgId: z.string().optional(),
  /** false (default) reports what would change without writing or deleting. */
  apply: z.boolean().default(false),
  /** Required to `apply` without an `orgId` — deleting across every tenant is explicit. */
  allOrgs: z.boolean().default(false),
  /** Actionable groups per call, largest saving first; re-call while `more`. */
  maxGroups: z.number().int().min(1).max(200).default(50),
  /** Ceiling on stored objects examined per call (one R2 head each), bounding subrequests. */
  maxKeys: z.number().int().min(1).max(2000).default(400),
});
export type EvidenceDeduplicationInput = z.infer<typeof EvidenceDeduplicationInputSchema>;

export const EvidenceDeduplicationResultSchema = z.object({
  applied: z.boolean(),
  groups: z.number().int(),
  rowsRepointed: z.number().int(),
  objectsFreed: z.number().int(),
  bytesReclaimed: z.number().int(),
  /** Locked rows repointed — byte-identical, sha256 unchanged. */
  lockedRowsRepointed: z.number().int(),
  /** Rows that pointed at an object no longer in R2 and now resolve again. */
  rowsRepaired: z.number().int(),
  /** Set when the run stopped on an error; work before it is done and audited. */
  stoppedEarly: z.string().nullable(),
  skipped: z.array(z.object({ orgId: z.string(), sha256: z.string(), reason: z.string() })),
  /** More groups matched than `maxGroups` — call again to continue. */
  more: z.boolean(),
  perOrg: z.array(
    z.object({
      orgId: z.string(),
      groups: z.number().int(),
      objectsFreed: z.number().int(),
      bytesReclaimed: z.number().int(),
    }),
  ),
});
export type EvidenceDeduplicationResult = z.infer<typeof EvidenceDeduplicationResultSchema>;

export const EVIDENCE_MAX_BYTES = 50 * 1024 * 1024;

/** Allowlisted upload content types (CONTRACTS.md §Evidence). */
export const EVIDENCE_ALLOWED_TYPES: ReadonlyArray<string> = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/heic',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/msword',
  'application/vnd.ms-outlook',
  'text/csv',
  'text/plain',
  'text/markdown',
  'text/html',
  'application/zip',
];
